import React, { useState, useRef, useEffect } from 'react';
import { API_BASE } from '../api';

export default function LPUpload() {
  const [participantId, setParticipantId] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0); // used to reset file input
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    // create / revoke preview URL
    if (file) {
      const url = URL.createObjectURL(file);
      setFilePreview(url);
      return () => {
        URL.revokeObjectURL(url);
        setFilePreview(null);
      };
    } else {
      setFilePreview(null);
    }
  }, [file]);

  const handleFiles = async (f) => {
    if (!f) return;
    const candidate = f;
    if (!candidate.type.startsWith('video/')) {
      setStatus('Please select a valid video file.');
      return;
    }
    setFile(candidate);
    setStatus('');
    // Auto-fill participant id from filename: take the first 3 characters if present.
    // Examples:
    //  - "123_video.mp4" -> participantId "123"
    //  - "12-video.mp4"  -> participantId "12" (will take what's available)
    let extracted = '';
    if (candidate && candidate.name) {
      const name = candidate.name;
      // Trim leading spaces just in case, then take up to 3 characters
      const trimmed = name.trim();
      extracted = trimmed.substring(0, 3);
      if (extracted) {
        setParticipantId(extracted);
      }
    }
    // Automatically upload after file is selected
    // Wait for participantId state to update if extracted
    setTimeout(() => {
      handleSubmit();
    }, 0);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dtFiles = e.dataTransfer && e.dataTransfer.files;
    if (dtFiles && dtFiles.length > 0) {
      handleFiles(dtFiles[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    handleFiles(f);
  };

  const resetForm = (clearStatus = true) => {
    setFile(null);
    setParticipantId('');
    if (clearStatus) setStatus('');
    // bump key to force <input type="file"> to reset value
    setUploadKey((k) => k + 1);
    // revoke preview handled by useEffect cleanup
  };

  // Allow handleSubmit to be called without an event (for auto-upload)
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!file) {
      setStatus('Please select a video file to upload.');
      return;
    }

    setUploading(true);
    setStatus('');

    const formData = new FormData();
    // Backend expects the video file under the field name "video"
    // If participantId is provided, create a new File instance with the destination filename
    // format "lp--XXX.mp4" so multer will save it as that name (it uses file.originalname).
    let uploadFile = file;
    if (participantId) {
      try {
        // Keep original mime type
        uploadFile = new File([file], `lp--${participantId}.mp4`, { type: file.type });
      } catch (e) {
        // In some environments File constructor can fail; fall back to original file
        // (upload will still succeed but filename may not be renamed client-side)
        // eslint-disable-next-line no-console
        console.warn('Could not rename file locally, uploading original filename', e);
        uploadFile = file;
      }
    }
    formData.append('video', uploadFile);
    // Include participant id as extra form field (backend can ignore if not needed)
    if (participantId) formData.append('participantId', participantId);

    try {
      const res = await fetch(`${API_BASE}/liveportraits`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        setStatus('Upload failed: ' + (err.error || res.statusText));
      } else {
        const data = await res.json();
        // Prefer the filename returned by the API. If only a URL is returned, extract the filename.
        const returnedFilename = data.filename || (data.url ? data.url.split('/').pop() : null);
        setStatus('Video succesfully uploaded as: ' + (returnedFilename || 'uploaded'));
        // Keep the status visible for a short moment so the user can read it,
        // then reset the form and preview after 3 seconds.
        setTimeout(() => {
          // Reset inputs but keep the success status visible
          resetForm(false);
        }, 3000);
      }
    } catch (err) {
      setStatus('Upload error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container" style={{ padding: '1.5rem' }}>
      <h1>Live Portrait Upload</h1>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="participantId" className="form-label">Participant ID</label>
          <input
            id="participantId"
            className="form-control"
            type="text"
            value={participantId}
            onChange={(e) => setParticipantId(e.target.value)}
            placeholder="Enter participant id"
          />
        </div>

        <div
          className="mb-3"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <label htmlFor="videoFile" className="form-label">Video file (drag & drop or click to choose)</label>
          <div
            style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '1rem',
              textAlign: 'center',
              background: dragActive ? '#f8f9fa' : 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {filePreview ? (
              <video
                src={filePreview}
                controls
                style={{ maxWidth: '100%', maxHeight: '300px', display: 'block', margin: '0 auto' }}
              />
            ) : (
              <div style={{ padding: '2rem 1rem', color: '#666' }}>
                <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
                  {dragActive ? 'Release to upload the video' : 'Drop a video file here or click to select'}
                </div>
                <small className="text-muted">Accepted: video files (mp4, webm, mov, ...)</small>
              </div>
            )}
            <input
              key={uploadKey}
              ref={fileInputRef}
              id="videoFile"
              className="form-control"
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
        {' '}
        <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={uploading}>
          Reset
        </button>
      </form>

      {status && (
        <div className="alert alert-secondary mt-3" role="status">
          {status}
        </div>
      )}
    </div>
  );
}
